import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import { useTranslation } from '../i18n/LanguageContext';
import LanguageSwitcher from './LanguageSwitcher';

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const { t } = useTranslation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      setIsMobileMenuOpen(false);
    }
  };

  return (
    <header 
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled 
          ? 'bg-white shadow-md' 
          : 'bg-transparent'
      }`}
    >
      <div className="container-cift">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center">
            <img 
              src={isScrolled ? "/logo-light.svg" : "/logo-dark.svg"}
              alt="CIFT Studio"
              className="h-16 w-auto"
              style={{ filter: isScrolled ? 'none' : 'brightness(0) invert(1)' }}
            />
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center space-x-8">
            <button 
              onClick={() => scrollToSection('business')}
              className={`label-text transition-colors ${
                isScrolled ? 'text-gray-600 hover:text-black' : 'text-white hover:text-cream'
              }`}
            >
              {t('nav.business')}
            </button>
            <button 
              onClick={() => scrollToSection('wedding')}
              className={`label-text transition-colors ${
                isScrolled ? 'text-gray-600 hover:text-black' : 'text-white hover:text-cream'
              }`}
            >
              {t('nav.weddings')}
            </button>
            <button 
              onClick={() => scrollToSection('portfolio')}
              className={`label-text transition-colors ${
                isScrolled ? 'text-gray-600 hover:text-black' : 'text-white hover:text-cream'
              }`}
            >
              {t('nav.portfolio')}
            </button>
            <button 
              onClick={() => scrollToSection('ablauf')}
              className={`label-text transition-colors ${
                isScrolled ? 'text-gray-600 hover:text-black' : 'text-white hover:text-cream'
              }`}
            >
              {t('nav.process')}
            </button>
            
            {/* Language Switcher */}
            <LanguageSwitcher isScrolled={isScrolled} />
            
            <button 
              onClick={() => scrollToSection('kontakt')}
              className={`text-sm font-semibold uppercase tracking-wider transition-all border px-6 py-2 ${
                isScrolled 
                  ? 'bg-transparent border-brand-dark text-brand-dark hover:bg-brand-dark hover:text-white' 
                  : 'bg-transparent border-white text-white hover:bg-white hover:text-brand-dark'
              }`}
            >
              {t('nav.contact')}
            </button>
          </nav>

          {/* Mobile Menu Button */}
          <button 
            className="md:hidden"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            aria-label="Toggle menu"
          >
            {isMobileMenuOpen ? (
              <X className={isScrolled ? 'text-black' : 'text-white'} size={28} />
            ) : (
              <Menu className={isScrolled ? 'text-black' : 'text-white'} size={28} />
            )}
          </button>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <nav className="md:hidden py-6 bg-white border-t border-gray-200">
            <div className="flex flex-col space-y-4">
              <button 
                onClick={() => scrollToSection('business')}
                className="label-text text-left px-4 py-2 hover:bg-gray-50 transition-colors"
              >
                {t('nav.business')}
              </button>
              <button 
                onClick={() => scrollToSection('wedding')}
                className="label-text text-left px-4 py-2 hover:bg-gray-50 transition-colors"
              >
                {t('nav.weddings')}
              </button>
              <button 
                onClick={() => scrollToSection('portfolio')}
                className="label-text text-left px-4 py-2 hover:bg-gray-50 transition-colors"
              >
                {t('nav.portfolio')}
              </button>
              <button 
                onClick={() => scrollToSection('ablauf')}
                className="label-text text-left px-4 py-2 hover:bg-gray-50 transition-colors"
              >
                {t('nav.process')}
              </button>
              
              {/* Language Switcher in Mobile Menu */}
              <div className="px-4 py-2">
                <LanguageSwitcher isScrolled={true} />
              </div>
              
              <button 
                onClick={() => scrollToSection('kontakt')}
                className="bg-transparent border border-brand-dark text-brand-dark hover:bg-brand-dark hover:text-white transition-all mx-4 mt-2 text-sm font-semibold uppercase tracking-wider px-6 py-2"
              >
                {t('nav.contact')}
              </button>
            </div>
          </nav>
        )}
      </div>
    </header>
  );
};

export default Header;
